import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, AuthResponse } from '../utils/auth';
import { getCurrentUserData } from '../utils/auth';
import { AUTH_UNAUTHORIZED_EVENT } from '../utils/authEvents';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  register: (data: any) => Promise<AuthResponse>;
  logout: () => Promise<AuthResponse>;
  forgotPassword: (email: string) => Promise<AuthResponse>;
  verifyOtp: (otp: string) => Promise<AuthResponse>;
  resetPassword: (data: any) => Promise<AuthResponse>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentUser = getCurrentUserData();
        setUser(currentUser);
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const { login: loginApi } = await import('../utils/auth');
      const response = await loginApi({ email, password });
      
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      
      return response;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: any): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const { register: registerApi } = await import('../utils/auth');
      const response = await registerApi(data);
      
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      
      return response;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async (): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const { logout: logoutApi } = await import('../utils/auth');
      const response = await logoutApi();
      
      if (response.success) {
        setUser(null);
      }
      
      return response;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const forgotPassword = async (email: string): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const { forgotPassword: forgotPasswordApi } = await import('../utils/auth');
      return await forgotPasswordApi(email);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (otp: string): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const { verifyOtp: verifyOtpApi } = await import('../utils/auth');
      return await verifyOtpApi(otp);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (data: any): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const { resetPassword: resetPasswordApi } = await import('../utils/auth');
      return await resetPasswordApi(data);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const { getProfile } = await import('../utils/auth');
      const response = await getProfile();
      
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      setUser(null);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, [logout]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    forgotPassword,
    verifyOtp,
    resetPassword,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
